import React, { useState, useEffect } from "react";
import { GenericError } from "@via/components/error.tsx";
import { SkeletonLoader } from "@via/components/loading/loading.tsx";
import { ERROR } from "@via/utils/constants";
import { StripeProvider } from "@via/context/stripe-context.tsx";
import CardSetup from "@via/pages/billing/card-setup.tsx";
import PaymentSetup from "@via/pages/billing/payment-setup.tsx";
import ConfirmSubscription from "@via/pages/billing/confirm-subscription.tsx";
import { useCreateSetupIntent } from "@via/pages/billing/billing.api.tsx";
import { useRedeemVoucherCode } from "@via/pages/billing/voucher-code.api.tsx";
import LoginLink from "@via/components/login-link.tsx";

interface StepProps {
  onNextStep: () => void;
  onPreviousStep: () => void;
  email: string;
  mode?: "card" | "payment";
  isUpgrade?: boolean;
}

const PaymentMethodForm: React.FC<StepProps> = ({
  onNextStep,
  onPreviousStep,
  email,
  mode = "payment",
  isUpgrade,
}) => {
  const [voucherEnabled, setVoucherEnabled] = useState(false);
  const [voucherValue, setVoucherValue] = useState("");
  const [paymentMethodId, setPaymentMethodId] = useState<string | null>(null);
  const [subStep, setSubStep] = useState<"payment" | "review">("payment");

  const {
    mutate: createSetupIntent,
    data,
    isPending,
    isError,
  } = useCreateSetupIntent();

  const { mutate: redeemCode, isPending: isRedeeming } = useRedeemVoucherCode();

  const handleCardConfirmed = (paymentMethodId: string) => {
    setPaymentMethodId(paymentMethodId);
    setSubStep("review");
  };

  const handleBack = () => {
    if (subStep === "review") {
      setPaymentMethodId(null);
      setSubStep("payment");
    } else {
      onPreviousStep();
    }
  };

  const handleVoucherRedeem = () => {
    if (!voucherValue.trim()) return;
    redeemCode(
      { code: voucherValue.trim() },
      { onSuccess: () => onNextStep() }
    );
  };

  useEffect(() => {
    if (subStep === "payment") {
      createSetupIntent(); // Always get a new clientSecret
    }
  }, [subStep, createSetupIntent]);

  useEffect(() => {
    if (data?.isStripeDisabled) {
      onNextStep();
    }
  }, [data, onNextStep]);

  return (
    <div className="bg-white">
      {isPending || (!data?.clientSecret && !data?.isStripeDisabled) ? (
        <div className="w-94 grid grid-cols-1 gap-6 sm:grid-cols-1 lg:grid-cols-1">
          <SkeletonLoader count={2} />
        </div>
      ) : isError ? (
        <div className="via-generic-error lg:py-12">
          <GenericError message={ERROR} />
        </div>
      ) : data?.isStripeDisabled ? (
        <div className="w-94 grid grid-cols-1 gap-6 sm:grid-cols-1 lg:grid-cols-1">
          <SkeletonLoader count={2} />
        </div>
      ) : (
        <StripeProvider clientSecret={data?.clientSecret || ""}>
          {subStep === "payment" ? (
            mode === "card" ? (
              <CardSetup
                email={email}
                clientSecret={data!.clientSecret!}
                onCardConfirmed={handleCardConfirmed}
                isUpgrade={isUpgrade}
                voucherCode={{
                  enabled: voucherEnabled,
                  onToggle: setVoucherEnabled,
                  value: voucherValue,
                  onChange: setVoucherValue,
                  onRedeem: handleVoucherRedeem,
                  isRedeeming,
                }}
              />
            ) : (
              <PaymentSetup onCardConfirmed={handleCardConfirmed} />
            )
          ) : (
            <ConfirmSubscription
              paymentMethodId={paymentMethodId!}
              onPreviousStep={handleBack}
              onNextStep={onNextStep}
            />
          )}
        </StripeProvider>
      )}
      <LoginLink className="mt-0" logoutBeforeLogin={true} />
    </div>
  );
};

export default PaymentMethodForm;
